import axios from 'axios';
import dayjs from 'dayjs';

import { SERVER_URL } from 'Constants';
import { AdditionalBookingOptionEntity } from 'Models/Entities';
import { serialiseDateTimeNoTz } from 'Util/AttributeUtils';
import { csvBlobFromText } from 'Util/_HumanWritten/FileDownloadUtils';
import { BookingFiltersOptions, CreateBookingFilterParams } from 'Util/_HumanWritten/Bookings/FetchBookingsWithFilters';
import { formatAsISO } from 'Util/_HumanWritten/DateUtils';
import { buildUrl } from 'Util/FetchUtils';
import { downloadFileFromUrl, getFilenameFromContentDisposition } from 'Util/_HumanWritten/FileDownloadUtils';
import alertToast from 'Util/ToastifyUtils';
import { Permission } from 'Models/Permissions';

/**
 * Shape returned by the export endpoints when they background the work (large datasets,
 * record count > server-side threshold). The 202 response is paired with this JSON so the
 * frontend can surface "your export will arrive by email" instead of trying to download a
 * non-existent CSV.
 */
interface BackgroundedExportResponse {
	status: 'queued';
	recordCount: number;
	deliveryEmail: string;
}

/**
 * Saves a CSV-shaped blob to disk under the supplied filename. Shared by every successful
 * (200) export download so each call site doesn't repeat the anchor-click dance.
 */
function downloadCsvBlob(data: Blob, fileName: string) {
	const blob = new Blob([data], { type: 'text/csv' });
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	window.URL.revokeObjectURL(url);
	document.body.removeChild(a);
}

/**
 * Parses the Content-Disposition header to find the filename the server chose. Falls back
 * to <paramref name="defaultName"/> if the header is absent or unparseable.
 */
function getDownloadFileName(contentDisposition: string | undefined, defaultName: string): string {
	return getFilenameFromContentDisposition(contentDisposition) ?? defaultName;
}

/**
 * Reads a 202 response's JSON body (which axios delivered as a Blob because we asked for
 * `responseType: 'blob'`) and surfaces a "your export will arrive by email" toast. Returns
 * silently on parse failure — better to no-op than crash the page over a notification.
 */
async function handleBackgroundedExportResponse(data: Blob) {
	try {
		const text = await data.text();
		const payload = JSON.parse(text) as BackgroundedExportResponse;
		alertToast(
			`Your export of ${payload.recordCount.toLocaleString()} records is too large to download directly. We'll email a download link to ${payload.deliveryEmail} when it's ready.`,
			'info',
			'Export queued',
			{ autoClose: 8000 },
		);
	} catch (err) {
		// Server told us 202 but we couldn't parse the body — still better to inform the
		// user than silently fail.
		alertToast('Your export is being prepared and will be emailed to you when ready.', 'info', 'Export queued', {
			autoClose: 8000,
		});
	}
}

/**
 * Surfaces an export failure instead of silently doing nothing. Because these requests ask for
 * responseType 'blob', an error body arrives as a Blob rather than text, so it has to be read back
 * before it can be shown — without this an authorisation or validation failure looks to the user
 * exactly like a button that does not work.
 */
async function reportExportFailure(error: unknown, context: string) {
	let message = 'Something went wrong generating the export. Please try again.';
	const data = (error as { response?: { data?: unknown } })?.response?.data;
	if (data instanceof Blob) {
		try {
			const text = await data.text();
			if (text) {
				message = text.slice(0, 300);
			}
		} catch {
			// keep the generic message
		}
	}
	alertToast(message, 'error', 'Export failed', { autoClose: 10000 });
	// eslint-disable-next-line no-console
	console.error(context, error);
}

/**
 * Saves a blob to disk under the supplied filename, preserving the server's content type (used for
 * non-CSV downloads like .xlsx / .pdf where forcing text/csv would corrupt the file).
 */
function downloadBlob(data: Blob, fileName: string) {
	const url = window.URL.createObjectURL(data);
	const a = document.createElement('a');
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	window.URL.revokeObjectURL(url);
	document.body.removeChild(a);
}

export type UtilisationExportFormat = 'csv' | 'excel' | 'pdf';

/**
 * Exports the utilisation report in the chosen format, honouring the current date range and
 * route/vessel filters. Small exports download inline; large ones return 202 and are emailed
 * (same handling as the transactions/bookings exports).
 */
export async function exportUtilisationReport(
	format: UtilisationExportFormat,
	start: Date,
	end: Date,
	routeIds: string[],
	ferryIds: string[],
	columns: string[] = [],
) {
	const params = new URLSearchParams();
	// Send local wall-clock without a timezone (matches the combined endpoint) so the server binds
	// the dates as Kind=Unspecified — a 'Z'/UTC timestamp is rejected by the timestamp-without-tz query.
	params.set('startDate', dayjs(start).startOf('day').format('YYYY-MM-DDTHH:mm:ss'));
	params.set('endDate', dayjs(end).endOf('day').format('YYYY-MM-DDTHH:mm:ss'));
	params.set('format', format);
	routeIds.forEach(id => params.append('routeIds', id));
	ferryIds.forEach(id => params.append('ferryIds', id));
	// Chosen columns from the on-screen picker (CSV/Excel honour these; empty = all).
	columns.forEach(key => params.append('columns', key));

	const ext = format === 'excel' ? 'xlsx' : format;

	try {
		const response = await axios.get(`${SERVER_URL}/api/reports/export-utilisation?${params.toString()}`, {
			responseType: 'blob',
			validateStatus: status => status === 200 || status === 202,
		});

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(response.headers['content-disposition'], `utilisation-export.${ext}`);
		downloadBlob(response.data, filename);
	} catch (error) {
		// Surface the failure instead of silently doing nothing. If the server sent an error body
		// (delivered as a Blob because we asked for responseType 'blob'), show its text.
		let message = 'Something went wrong generating the export. Please try again.';
		const data = (error as { response?: { data?: unknown } })?.response?.data;
		if (data instanceof Blob) {
			try {
				const text = await data.text();
				if (text) {
					message = text.slice(0, 300);
				}
			} catch {
				// keep the generic message
			}
		}
		alertToast(message, 'error', 'Export failed', { autoClose: 10000 });
		// eslint-disable-next-line no-console
		console.error('Error exporting utilisation report:', error);
	}
}

export interface GraphExportPayload {
	title: string;
	categoryHeader: string;
	/** Match the on-screen chart type in the Excel export: 'line' or 'bar'. */
	chartType: 'line' | 'bar';
	categories: string[];
	/**
	 * A null value means "no figure for this category" (e.g. a day on which nothing sailed) and is written
	 * as a blank cell in the CSV. The Excel path flattens nulls to 0 — see {@link exportGraphExcel}.
	 */
	series: Array<{ name: string; values: Array<number | null> }>;
}

function csvEscape(value: string): string {
	return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Downloads a single graph's underlying data as a CSV, built client-side. */
export function downloadGraphCsv(payload: GraphExportPayload, fileBaseName: string) {
	const header = [payload.categoryHeader, ...payload.series.map(s => s.name)];
	const lines = [header.map(csvEscape).join(',')];
	payload.categories.forEach((category, i) => {
		const row = [csvEscape(category), ...payload.series.map(s => String(s.values[i] ?? ''))];
		lines.push(row.join(','));
	});
	downloadBlob(csvBlobFromText(lines.join('\n')), `${fileBaseName}.csv`);
}

/** Exports a single graph as an .xlsx (data + a native chart) via the backend. */
export async function exportGraphExcel(payload: GraphExportPayload, fileBaseName: string) {
	try {
		// The endpoint binds values to List<double>, so a null would fail model binding. Flatten gaps to 0
		// for this path only; the CSV above keeps them blank.
		const body: GraphExportPayload = {
			...payload,
			series: payload.series.map(s => ({ ...s, values: s.values.map(v => v ?? 0) })),
		};
		const response = await axios.post(`${SERVER_URL}/api/reports/export-graph-excel`, body, {
			responseType: 'blob',
			validateStatus: status => status === 200,
		});
		downloadBlob(response.data, `${fileBaseName}.xlsx`);
	} catch (error) {
		alertToast('Could not export the graph to Excel. Please try again.', 'error', 'Export failed', {
			autoClose: 8000,
		});
		// eslint-disable-next-line no-console
		console.error('Error exporting graph to Excel:', error);
	}
}

interface LoadRevenueReportCsvResponse {
	message: string;
	email: boolean;
}

export async function loadRevenueReportCsv(
	start: Date,
	end?: Date,
	unrealised = false,
): Promise<LoadRevenueReportCsvResponse | void> {
	const startDate = formatAsISO(new Date(start.getFullYear(), start.getMonth(), start.getDate()));
	// Unrealised (forward) revenue may be open-ended — omit the end date so the server runs it to
	// the end of time.
	const endDate = end ? formatAsISO(new Date(end.getFullYear(), end.getMonth(), end.getDate())) : undefined;
	const apiUrl = buildUrl(`${SERVER_URL}/api/reports/export-revenue-report`, {
		startDate,
		...(endDate ? { endDate } : {}),
		unrealised: unrealised ? 'true' : 'false',
	});

	try {
		const response = await axios.get<Blob | any>(apiUrl, {
			responseType: 'blob',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		// Accepted status returns LoadRevenueReportCsvResponse
		if (response.status === 202) {
			const text = await (response.data as Blob).text();
			const emailResponse: LoadRevenueReportCsvResponse = JSON.parse(text);
			return emailResponse;
		}

		// Get the filename from the response headers if available
		const filename = getDownloadFileName(response.headers['content-disposition'], 'selected-bookings.csv');

		// Create blob and trigger download
		const blob = new Blob([response.data], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();

		// Cleanup
		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
	} catch (error) {
		console.error('Error exporting selected bookings:', error);
	}
}

export async function downloadLedgerReport(start: Date, end: Date): Promise<LoadRevenueReportCsvResponse | void> {
	const startDate = formatAsISO(new Date(start.getFullYear(), start.getMonth(), start.getDate()));
	const endDate = formatAsISO(new Date(end.getFullYear(), end.getMonth(), end.getDate()));
	const apiUrl = buildUrl(`${SERVER_URL}/api/reports/export-ledger-report`, {
		startDate,
		endDate,
		format: 'xlsx',
	});

	try {
		const response = await axios.get<Blob | any>(apiUrl, {
			responseType: 'blob',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		// Accepted status returns LoadRevenueReportCsvResponse
		if (response.status === 202) {
			const text = await (response.data as Blob).text();
			const emailResponse: LoadRevenueReportCsvResponse = JSON.parse(text);
			return emailResponse;
		}

		// Get the filename from the response headers if available
		const filename = getDownloadFileName(response.headers['content-disposition'], 'ledger_report.xlsx');

		// Preserve the server's content type (.xlsx) and trigger download
		downloadBlob(response.data, filename);
	} catch (error) {
		console.error('Error exporting ledger report:', error);
		// The ledger is a slow export over a user-chosen range, so a silent failure reads as a hung
		// browser. Every other path here surfaces its outcome; this one has to as well.
		alertToast('The ledger report could not be generated. Please try again.', 'error', 'Export failed');
	}
}

export async function downloadTakingsBreakdownReportCsv(
	start: Date,
	end: Date,
): Promise<LoadRevenueReportCsvResponse | void> {
	const startDate = formatAsISO(new Date(start.getFullYear(), start.getMonth(), start.getDate()));
	const endDate = formatAsISO(new Date(end.getFullYear(), end.getMonth(), end.getDate()));
	const apiUrl = buildUrl(`${SERVER_URL}/api/reports/export-income-report-report`, {
		startDate,
		endDate,
	});

	try {
		const response = await axios.get<Blob | any>(apiUrl, {
			responseType: 'blob',
			headers: {
				'Content-Type': 'application/json',
			},
			// Large income reports return 202 and are emailed; small ones stream the CSV inline.
			validateStatus: status => status === 200 || status === 202,
		});

		// Accepted status returns LoadRevenueReportCsvResponse
		if (response.status === 202) {
			const text = await (response.data as Blob).text();
			const emailResponse: LoadRevenueReportCsvResponse = JSON.parse(text);
			return emailResponse;
		}

		// Get the filename from the response headers if available
		const contentDisposition = response.headers['content-disposition'];
		let filename = 'income_report.csv';

		if (contentDisposition) {
			const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
			if (filenameMatch && filenameMatch[1]) {
				filename = filenameMatch[1].replace(/['"]/g, '');
			}
		}

		// Create blob and trigger download
		const blob = new Blob([response.data], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();

		// Cleanup
		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
	} catch (error) {
		console.error('Error exporting income report:', error);
	}
}

export async function downloadInvoiceReport(
	start: Date,
	end: Date,
	fetchByCreated: boolean,
	markAsInvoiced = false,
	itemised = false,
): Promise<boolean> {
	const startDate = formatAsISO(new Date(start.getFullYear(), start.getMonth(), start.getDate()));
	const endDate = formatAsISO(new Date(end.getFullYear(), end.getMonth(), end.getDate()));

	if (!markAsInvoiced) {
		const url = buildUrl(`${SERVER_URL}/api/reports/export-invoice-report`, {
			startDate,
			endDate,
			fetchByCreated: String(fetchByCreated),
			itemised: String(itemised),
		});

		window.open(url, '_blank')?.focus();
		return true;
	}

	const response = await axios.post(
		`${SERVER_URL}/api/reports/export-invoice-report-and-mark-invoiced`,
		{
			startDate,
			endDate,
			fetchByCreated,
			itemised,
		},
		{ responseType: 'blob' },
	);

	const filename = getDownloadFileName(response.headers['content-disposition'], 'invoice-report.csv');

	const blob = new Blob([response.data], { type: 'text/csv' });
	const objectUrl = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = objectUrl;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	window.URL.revokeObjectURL(objectUrl);
	document.body.removeChild(a);

	return true;
}

/**
 * Builds a cash/eftpos report export URL. These reports take an optional `userIds` filter
 * (the staff who took the payment), which is a repeated query param — buildUrl only handles
 * scalar params, so we append the array entries manually onto the base URL.
 */
function buildReportExportUrl(path: string, startDate: string, endDate: string, userIds?: string[]): string {
	const params = new URLSearchParams({ startDate, endDate });
	(userIds ?? []).forEach(id => params.append('userIds', id));
	return `${SERVER_URL}${path}?${params.toString()}`;
}

export function downloadCashReport(start: Date, end: Date, userIds?: string[]) {
	// Send the wall-clock time the user picked WITHOUT a timezone marker (see serialiseDateTimeNoTz):
	// formatAsISO tags local time with a 'Z', so the server would treat it as UTC and re-localise it
	// to the report timezone, shifting the window by the offset (e.g. dropping the first 10 hours in
	// AEST). This is a time-of-day report, so the exact wall-clock must survive to the server.
	const url = buildReportExportUrl(
		'/api/reports/export-cash-report',
		serialiseDateTimeNoTz(start),
		serialiseDateTimeNoTz(end),
		userIds,
	);

	window.open(url, '_blank')?.focus();
}

export function downloadPaymentsTakenReport(start: Date, end: Date) {
	const startDate = formatAsISO(new Date(start.getFullYear(), start.getMonth(), start.getDate()));
	const endDate = formatAsISO(new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59));

	const url = buildUrl(`${SERVER_URL}/api/reports/export-payments-taken-report`, {
		startDate,
		endDate,
	});

	window.open(url, '_blank')?.focus();
}

export function downloadEftposReport(start: Date, end: Date, userIds?: string[]) {
	// Timezone-less wall-clock, same reasoning as downloadCashReport (this is also a time-of-day report).
	const url = buildReportExportUrl(
		'/api/reports/export-eftpos-report',
		serialiseDateTimeNoTz(start),
		serialiseDateTimeNoTz(end),
		userIds,
	);

	window.open(url, '_blank')?.focus();
}

export interface StaffUser {
	id: string;
	firstName?: string;
	lastName?: string;
	email: string;
}

export interface StaffUsersPage {
	users: StaffUser[];
	total: number;
	hasMore: boolean;
}

/** Staff role names accepted as a minimum-level filter, lowest to highest. */
export type StaffMinimumRole = 'Staff' | 'Manager' | 'Admin' | 'Super Administrators';

export interface GetStaffUsersParams {
	search?: string;
	pageNo: number;
	pageSize?: number;
	/** Only include users with this role or higher. Defaults to Staff (all staff+ users). */
	minimumRole?: StaffMinimumRole;
	/** When true, omit anyone holding the Super Administrators role (even with a lower role too). */
	excludeSuperAdministrators?: boolean;
	/**
	 * When set, only users who hold this capability — grants included.
	 *
	 * Use it wherever the question is "who could actually act on this". `minimumRole` answers a
	 * different one: it cannot see someone granted the capability individually, which is the whole
	 * point of having per-user grants. The two compose, so passing both narrows by each.
	 */
	withPermission?: Permission;
}

/**
 * Fetches a page of staff+ users (Super Administrators, Admin, Manager, Staff) used to
 * populate staff pickers — e.g. the "taken by" filter on the cash / EFTPOS report exports,
 * or the user-type notification receiver picker (which passes withPermission).
 * Backed by the dedicated, lightweight /api/account/staff-users endpoint, which supports
 * paging (for infinite scroll) and a case-insensitive search over name + email.
 */
export async function getStaffUsers({
	search,
	pageNo,
	pageSize = 25,
	minimumRole = 'Staff',
	excludeSuperAdministrators,
	withPermission,
}: GetStaffUsersParams): Promise<StaffUsersPage> {
	const response = await axios.get<{ users?: StaffUser[]; total?: number; hasMore?: boolean }>(
		`${SERVER_URL}/api/account/staff-users`,
		{
			params: {
				search: search?.trim() || undefined,
				pageNo,
				pageSize,
				minimumRole,
				excludeSuperAdministrators: excludeSuperAdministrators || undefined,
				withPermission,
			},
			validateStatus: () => true,
		},
	);

	return {
		users: Array.isArray(response.data?.users) ? response.data!.users! : [],
		total: response.data?.total ?? 0,
		hasMore: response.data?.hasMore ?? false,
	};
}

export function downloadDiscountReport(start: Date, end: Date) {
	const startDate = formatAsISO(new Date(start.getFullYear(), start.getMonth(), start.getDate()));
	const endDate = formatAsISO(new Date(end.getFullYear(), end.getMonth(), end.getDate()));

	const url = buildUrl(`${SERVER_URL}/api/reports/export-discount-report`, {
		startDate,
		endDate,
	});

	window.open(url, '_blank')?.focus();
}

export function downloadOccupancyReport(start: Date, end: Date) {
	const startDate = formatAsISO(new Date(start.getFullYear(), start.getMonth(), start.getDate()));
	const endDate = formatAsISO(new Date(end.getFullYear(), end.getMonth(), end.getDate()));

	const url = buildUrl(`${SERVER_URL}/api/reports/export-occupancy-report`, {
		startDate,
		endDate,
	});

	window.open(url, '_blank')?.focus();
}

export function downloadOperationReport(start: Date, end: Date) {
	const startDate = formatAsISO(new Date(start.getFullYear(), start.getMonth(), start.getDate()));
	const endDate = formatAsISO(new Date(end.getFullYear(), end.getMonth(), end.getDate()));

	const url = buildUrl(`${SERVER_URL}/api/reports/export-operation-report`, {
		startDate,
		endDate,
	});

	window.open(url, '_blank')?.focus();
}
export async function downloadAdditionalOptionReport(
	start: Date,
	end: Date,
	fetchByCreated: boolean,
	additionalOptionId: string,
): Promise<void> {
	const startDate = formatAsISO(new Date(start.getFullYear(), start.getMonth(), start.getDate()));
	const endDate = formatAsISO(new Date(end.getFullYear(), end.getMonth(), end.getDate()));

	const url = buildUrl(`${SERVER_URL}/api/reports/export-additional-option`, {
		startDate,
		endDate,
		fetchByCreated: String(fetchByCreated),
		additionalOptionId,
	});

	window.open(url, '_blank')?.focus();
}

export async function getReportableAdditionalBookingOptions(): Promise<AdditionalBookingOptionEntity[]> {
	const response = await axios.get(`${SERVER_URL}/api/reports/get-reportable-additional-options`, {
		validateStatus: () => true,
	});

	return response.data;
}

export async function exportBookingsWithFilters(options: BookingFiltersOptions) {
	const params = CreateBookingFilterParams(options);

	try {
		// Same 200/202 dual-response handling as the users / transactions exports — large
		// bookings exports background and email a link; small ones stream the CSV inline.
		const response = await axios.get(`${SERVER_URL}/api/reports/export-filtered-bookings`, {
			params,
			responseType: 'blob',
			validateStatus: status => status === 200 || status === 202,
		});

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(response.headers['content-disposition'], 'filtered-bookings.csv');
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		await reportExportFailure(error, 'Error exporting bookings:');
	}
}

export interface MyBookingsExportOptions {
	filters: BookingFiltersOptions;
	parkingColumns: string[];
	includeBookings: boolean;
	includeParking: boolean;
}

/**
 * The customer's own export, from the bookings page. Unlike the staff one this returns a
 * multi-section file — ferry bookings, event bookings and parking reservations each with their own
 * header row — so it has its own endpoint rather than a flag on the other.
 *
 * There is no background-and-email path for it: a file this size is built inline, and the server
 * answers 413 rather than spending two minutes on an export nobody waited for.
 */
export async function exportMyBookings(options: MyBookingsExportOptions): Promise<void> {
	const params = CreateBookingFilterParams(options.filters);
	options.parkingColumns.forEach(key => params.append('parkingColumns', key));
	params.append('includeBookings', String(options.includeBookings));
	params.append('includeParking', String(options.includeParking));

	try {
		const response = await axios.get(`${SERVER_URL}/api/reports/export-my-bookings`, {
			params,
			responseType: 'blob',
			validateStatus: status => status === 200,
		});

		const filename = getDownloadFileName(response.headers['content-disposition'], 'my-bookings.csv');
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		const status = (error as { response?: { status?: number } })?.response?.status;
		if (status === 413) {
			alertToast(
				'Your bookings are too many to export in one file. Please narrow the export using the filters above.',
				'error',
				'Export too large',
				{ autoClose: 10000 },
			);
			return;
		}
		await reportExportFailure(error, 'Error exporting my bookings:');
	}
}

export async function exportSelectedBookings(bookingIds: string[], columns: string[]): Promise<void> {
	try {
		const response = await axios.post(
			`${SERVER_URL}/api/reports/export-selected-bookings`,
			{
				bookingIds,
				columns,
			},
			{
				responseType: 'blob',
				headers: {
					'Content-Type': 'application/json',
				},
				validateStatus: status => status === 200 || status === 202,
			},
		);

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(response.headers['content-disposition'], 'selected-bookings.csv');
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		await reportExportFailure(error, 'Error exporting selected bookings:');
	}
}

export interface UserExportOptions {
	searchTerm?: string;
	userRoles?: string[];
	userDisplayNames?: string[];
	columns: string[];
	statusFilter?: string[];
	verifiedFilter?: boolean | null;
	twoFactorFilter?: boolean | null;
	marketingSubscribedFilter?: boolean | null;
	includeSuperUsers?: boolean;
	startDate?: Date;
	endDate?: Date;
}

export async function exportUsersWithFilters(options: UserExportOptions): Promise<void> {
	const params = new URLSearchParams();

	if (options.searchTerm) {
		params.append('searchTerm', options.searchTerm);
	}

	if (options.userRoles) {
		options.userRoles.forEach(role => params.append('userRoles', role));
	}

	if (options.userDisplayNames) {
		options.userDisplayNames.forEach(name => params.append('userDisplayNames', name));
	}

	if (options.columns) {
		options.columns.forEach(col => params.append('columns', col));
	}

	if (options.statusFilter) {
		options.statusFilter.forEach(s => params.append('statusFilter', s));
	}

	if (options.verifiedFilter !== undefined && options.verifiedFilter !== null) {
		params.append('verifiedFilter', String(options.verifiedFilter));
	}

	if (options.twoFactorFilter !== undefined && options.twoFactorFilter !== null) {
		params.append('twoFactorFilter', String(options.twoFactorFilter));
	}

	if (options.marketingSubscribedFilter !== undefined && options.marketingSubscribedFilter !== null) {
		params.append('marketingSubscribedFilter', String(options.marketingSubscribedFilter));
	}

	if (options.includeSuperUsers) {
		params.append('includeSuperUsers', 'true');
	}

	if (options.startDate) {
		params.append('startDate', options.startDate.toISOString());
	}

	if (options.endDate) {
		params.append('endDate', options.endDate.toISOString());
	}

	try {
		// Use axios (instead of window.open) so we can detect the 202 Accepted response the
		// server returns for large exports — the browser would just render the JSON status
		// payload otherwise. responseType: 'blob' so the CSV path stays a binary stream.
		const response = await axios.get(`${SERVER_URL}/api/reports/export-users`, {
			params,
			responseType: 'blob',
			// Don't let 202 land in the catch — we treat it as success.
			validateStatus: status => status === 200 || status === 202,
		});

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(response.headers['content-disposition'], 'users-export.csv');
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		console.error('Error exporting users:', error);
	}
}

export async function exportSelectedUsers(userIds: string[], columns: string[]): Promise<void> {
	try {
		const response = await axios.post(
			`${SERVER_URL}/api/reports/export-selected-users`,
			{
				userIds,
				columns,
			},
			{
				responseType: 'blob',
				headers: {
					'Content-Type': 'application/json',
				},
				// Same 202 handling as the filtered export above — the server backgrounds
				// when the row count exceeds the streaming threshold.
				validateStatus: status => status === 200 || status === 202,
			},
		);

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(response.headers['content-disposition'], 'selected-users.csv');
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		console.error('Error exporting selected users:', error);
	}
}

export interface TransactionExportOptions {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	filterByCreatedDate?: boolean;
	statusFilters?: string[];
	locationFilters?: string[];
	createdByFilters?: string[];
	typeFilters?: string[];
	actionFilters?: string[];
	columns: string[];
}

export async function exportTransactionsWithFilters(options: TransactionExportOptions): Promise<void> {
	const params = new URLSearchParams();

	if (options.searchString) {
		params.append('searchString', options.searchString);
	}

	if (options.startDate) {
		params.append('startDate', serialiseDateTimeNoTz(options.startDate));
	}

	if (options.endDate) {
		params.append('endDate', serialiseDateTimeNoTz(options.endDate));
	}

	if (options.filterByCreatedDate !== undefined) {
		params.append('filterByCreatedDate', String(options.filterByCreatedDate));
	}

	if (options.statusFilters) {
		options.statusFilters.forEach(s => params.append('statusFilters', s));
	}

	if (options.locationFilters) {
		options.locationFilters.forEach(l => params.append('locationFilters', l));
	}

	if (options.createdByFilters) {
		options.createdByFilters.forEach(cb => params.append('createdByFilters', cb));
	}

	if (options.typeFilters) {
		options.typeFilters.forEach(t => params.append('typeFilters', t));
	}

	if (options.actionFilters) {
		options.actionFilters.forEach(a => params.append('actionFilters', a));
	}

	if (options.columns) {
		options.columns.forEach(col => params.append('columns', col));
	}

	try {
		// Same 200/202 dual-response handling as the users export — large transactions
		// exports background and email a link; small ones stream the CSV inline.
		const response = await axios.get(`${SERVER_URL}/api/reports/export-transactions`, {
			params,
			responseType: 'blob',
			validateStatus: status => status === 200 || status === 202,
		});

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(response.headers['content-disposition'], 'transactions-export.csv');
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		console.error('Error exporting transactions:', error);
	}
}

export async function exportSelectedTransactions(transactionIds: string[], columns: string[]): Promise<void> {
	try {
		const response = await axios.post(
			`${SERVER_URL}/api/reports/export-selected-transactions`,
			{
				transactionIds,
				columns,
			},
			{
				responseType: 'blob',
				headers: {
					'Content-Type': 'application/json',
				},
				validateStatus: status => status === 200 || status === 202,
			},
		);

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(response.headers['content-disposition'], 'selected-transactions.csv');
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		console.error('Error exporting selected transactions:', error);
	}
}

export interface FeeExportOptions {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	feeTypeFilters?: string[];
	refundableFilters?: string[];
	waivedFilters?: string[];
	columns: string[];
}

export async function exportFeesWithFilters(options: FeeExportOptions): Promise<void> {
	const params = new URLSearchParams();

	if (options.searchString) {
		params.append('searchString', options.searchString);
	}

	if (options.startDate) {
		params.append('startDate', serialiseDateTimeNoTz(options.startDate));
	}

	if (options.endDate) {
		params.append('endDate', serialiseDateTimeNoTz(options.endDate));
	}

	if (options.feeTypeFilters) {
		options.feeTypeFilters.forEach(f => params.append('feeTypeFilters', f));
	}

	if (options.refundableFilters) {
		options.refundableFilters.forEach(r => params.append('refundableFilters', r));
	}

	if (options.waivedFilters) {
		options.waivedFilters.forEach(w => params.append('waivedFilters', w));
	}

	if (options.columns) {
		options.columns.forEach(col => params.append('columns', col));
	}

	try {
		// The fees export streams the CSV inline (200); the 202 background-and-email path is handled
		// too for consistency with the other exports, though the fees endpoint only returns 200.
		const response = await axios.get(`${SERVER_URL}/api/reports/export-fees`, {
			params,
			responseType: 'blob',
			validateStatus: status => status === 200 || status === 202,
		});

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(response.headers['content-disposition'], 'fees-export.csv');
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		console.error('Error exporting fees:', error);
	}
}

export async function exportSelectedFees(feeIds: string[], columns: string[]): Promise<void> {
	try {
		const response = await axios.post(
			`${SERVER_URL}/api/reports/export-selected-fees`,
			{
				feeIds,
				columns,
			},
			{
				responseType: 'blob',
				headers: {
					'Content-Type': 'application/json',
				},
				validateStatus: status => status === 200 || status === 202,
			},
		);

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(response.headers['content-disposition'], 'selected-fees.csv');
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		console.error('Error exporting selected fees:', error);
	}
}

export interface FerryTripExportOptions {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	routeFilters?: string[];
	ferryFilters?: string[];
	statusFilters?: string[];
	columns?: string[];
}

export async function exportFerryTripsWithFilters(options: FerryTripExportOptions): Promise<void> {
	const params = new URLSearchParams();

	if (options.searchString) {
		params.append('searchString', options.searchString);
	}
	if (options.startDate) {
		params.append('startDate', serialiseDateTimeNoTz(options.startDate));
	}
	if (options.endDate) {
		params.append('endDate', serialiseDateTimeNoTz(options.endDate));
	}
	if (options.routeFilters) {
		options.routeFilters.forEach(r => params.append('routeFilters', r));
	}
	if (options.ferryFilters) {
		options.ferryFilters.forEach(f => params.append('ferryFilters', f));
	}
	if (options.statusFilters) {
		options.statusFilters.forEach(s => params.append('statusFilters', s));
	}
	if (options.columns) {
		options.columns.forEach(col => params.append('columns', col));
	}

	try {
		// Same 200/202 dual-response handling as the transactions / bookings exports — large
		// ferry-trip exports background and email a link; small ones stream the CSV inline.
		const response = await axios.get(`${SERVER_URL}/api/reports/export-ferry-trips`, {
			params,
			responseType: 'blob',
			validateStatus: status => status === 200 || status === 202,
		});

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(response.headers['content-disposition'], 'ferry-trips-export.csv');
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		console.error('Error exporting ferry trips:', error);
	}
}

export async function exportSelectedFerryTrips(tripIds: string[], columns: string[]): Promise<void> {
	try {
		const response = await axios.post(
			`${SERVER_URL}/api/reports/export-selected-ferry-trips`,
			{
				tripIds,
				columns,
			},
			{
				responseType: 'blob',
				headers: {
					'Content-Type': 'application/json',
				},
				validateStatus: status => status === 200 || status === 202,
			},
		);

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(response.headers['content-disposition'], 'selected-ferry-trips.csv');
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		console.error('Error exporting selected ferry trips:', error);
	}
}

export interface ParkingReservationExportOptions {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	dateFilterType?: string;
	statusFilters?: string[];
	locationFilters?: string[];
	columns: string[];
}

export async function exportParkingReservationsWithFilters(options: ParkingReservationExportOptions): Promise<void> {
	const params = new URLSearchParams();

	if (options.searchString) {
		params.append('searchString', options.searchString);
	}

	if (options.startDate) {
		params.append('startDate', serialiseDateTimeNoTz(options.startDate));
	}

	if (options.endDate) {
		params.append('endDate', serialiseDateTimeNoTz(options.endDate));
	}

	if (options.dateFilterType) {
		params.append('dateFilterType', options.dateFilterType);
	}

	if (options.statusFilters) {
		options.statusFilters.forEach(s => params.append('statusFilters', s));
	}

	if (options.locationFilters) {
		options.locationFilters.forEach(l => params.append('locationFilters', l));
	}

	if (options.columns) {
		options.columns.forEach(col => params.append('columns', col));
	}

	try {
		// Same 200/202 dual-response handling as the other exports — large parking
		// exports background and email a link; small ones stream the CSV inline.
		const response = await axios.get(`${SERVER_URL}/api/reports/export-parking-reservations`, {
			params,
			responseType: 'blob',
			validateStatus: status => status === 200 || status === 202,
		});

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(
			response.headers['content-disposition'],
			'parking-reservations-export.csv',
		);
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		console.error('Error exporting parking reservations:', error);
	}
}

export async function exportSelectedParkingReservations(reservationIds: string[], columns: string[]): Promise<void> {
	try {
		const response = await axios.post(
			`${SERVER_URL}/api/reports/export-selected-parking-reservations`,
			{
				reservationIds,
				columns,
			},
			{
				responseType: 'blob',
				headers: {
					'Content-Type': 'application/json',
				},
				validateStatus: status => status === 200 || status === 202,
			},
		);

		if (response.status === 202) {
			await handleBackgroundedExportResponse(response.data);
			return;
		}

		const filename = getDownloadFileName(
			response.headers['content-disposition'],
			'selected-parking-reservations.csv',
		);
		downloadCsvBlob(response.data, filename);
	} catch (error) {
		console.error('Error exporting selected parking reservations:', error);
	}
}

export interface VoucherExportOptions {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	filterByCreatedDate?: boolean;
	typeFilters?: string[];
	statusFilters?: string[];
	columns: string[];
}

export async function exportVouchersWithFilters(options: VoucherExportOptions): Promise<void> {
	const params = new URLSearchParams();

	if (options.searchString) {
		params.append('searchString', options.searchString);
	}

	if (options.startDate) {
		params.append('startDate', options.startDate.toISOString());
	}

	if (options.endDate) {
		params.append('endDate', options.endDate.toISOString());
	}

	if (options.filterByCreatedDate !== undefined) {
		params.append('filterByCreatedDate', String(options.filterByCreatedDate));
	}

	if (options.typeFilters) {
		options.typeFilters.forEach(t => params.append('typeFilters', t));
	}

	if (options.statusFilters) {
		options.statusFilters.forEach(s => params.append('statusFilters', s));
	}

	if (options.columns) {
		options.columns.forEach(col => params.append('columns', col));
	}

	const url = `${SERVER_URL}/api/reports/export-vouchers?${params.toString()}`;
	window.open(url, '_blank')?.focus();
}

export async function exportSelectedVouchers(voucherIds: string[], columns: string[]): Promise<void> {
	try {
		const response = await axios.post(
			`${SERVER_URL}/api/reports/export-selected-vouchers`,
			{
				voucherIds,
				columns,
			},
			{
				responseType: 'blob',
				headers: {
					'Content-Type': 'application/json',
				},
			},
		);

		const filename = getDownloadFileName(response.headers['content-disposition'], 'selected-vouchers.csv');

		const blob = new Blob([response.data], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();

		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
	} catch (error) {
		console.error('Error exporting selected vouchers:', error);
	}
}

export interface RefundExportOptions {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	statusFilters?: string[];
	methodFilters?: string[];
	columns: string[];
}

export async function exportRefundsWithFilters(options: RefundExportOptions): Promise<void> {
	const params = new URLSearchParams();

	if (options.searchString) {
		params.append('searchString', options.searchString);
	}
	if (options.startDate) {
		params.append('startDate', options.startDate.toISOString());
	}
	if (options.endDate) {
		params.append('endDate', options.endDate.toISOString());
	}
	(options.statusFilters ?? []).forEach(s => params.append('statusFilters', s));
	(options.methodFilters ?? []).forEach(m => params.append('methodFilters', m));
	(options.columns ?? []).forEach(c => params.append('columns', c));

	const url = `${SERVER_URL}/api/reports/export-refunds?${params.toString()}`;
	window.open(url, '_blank')?.focus();
}

export async function exportSelectedRefunds(refundIds: string[], columns: string[]): Promise<void> {
	try {
		const response = await axios.post(
			`${SERVER_URL}/api/reports/export-selected-refunds`,
			{ refundIds, columns },
			{
				responseType: 'blob',
				headers: { 'Content-Type': 'application/json' },
			},
		);

		const filename = getDownloadFileName(response.headers['content-disposition'], 'selected-refunds.csv');

		const blob = new Blob([response.data], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
	} catch (error) {
		console.error('Error exporting selected refunds:', error);
	}
}

export interface UserEntitlementExportOptions {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	filterByCreatedDate?: boolean;
	productIds?: string[];
	statusFilters?: string[];
	columns: string[];
}

/**
 * Downloads the anytime-tickets table as CSV for the filters currently applied.
 *
 * Follows the transactions export rather than the admin CRUD ones: the filters and the chosen columns
 * are sent, so the file matches the screen. The server re-runs the same query the table uses, so the
 * rows cannot drift from what was on display.
 */
export async function exportUserEntitlementsWithFilters(options: UserEntitlementExportOptions): Promise<void> {
	const params = new URLSearchParams();

	if (options.searchString) {
		params.append('searchString', options.searchString);
	}
	if (options.startDate) {
		params.append('startDate', serialiseDateTimeNoTz(options.startDate));
	}
	if (options.endDate) {
		params.append('endDate', serialiseDateTimeNoTz(options.endDate));
	}
	if (options.filterByCreatedDate !== undefined) {
		params.append('filterByCreatedDate', String(options.filterByCreatedDate));
	}
	options.productIds?.forEach(id => params.append('productIds', id));
	options.statusFilters?.forEach(status => params.append('statusFilters', status));
	options.columns.forEach(column => params.append('columns', column));

	await downloadFileFromUrl(`${SERVER_URL}/api/reports/export-anytime-tickets`, 'anytime-tickets.csv', params);
}
